import { Fragment, useEffect, useState } from 'react';
import { FaUserAltSlash } from 'react-icons/fa';
import { FiExternalLink } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import useGetParams from '../hooks/useGetParams';
import SellersTable from '../components/SellersTable';
import UserDTO, {
  UserWithBusiness,
} from '../models/user';
import {
  useGetBusinessUsersQuery,
  useGetEventUsersQuery,
} from '../store';
import IconInfoCard from '../components/atoms/IconInfoCard';
import CustomModal from '../components/atoms/CustomModal';
import AddEditSellerToEvent from '../components/AddEditSellerToEvent';
import ImportSellersToEvent from '../components/ImportSellersToEvent';
import AdminSectionTitle from '../components/AdminSectionTitle';
import useGetSelectedEvent from '../hooks/useGetSelectedEvent';
import Button from '../components/atoms/Button';
import TopSellersRanking from '../components/TopSellersRanking';
import ArrowDownCircle from '../components/icons/ArrowDownCircle';
import OrdersTableSkeleton from '../components/OrdersTableSkeleton';

const getSellersNotInEvent = (
  users: UserDTO[],
  businessId: string,
  eventId: string,
) => {
  const bizUsers: UserWithBusiness[] = users
    .map((u) => ({
      ...u,
      user_business: u.user_businesses.find(
        (ub) => ub.business_id === businessId,
      ),
    }))
    .filter((u): u is UserWithBusiness => u.user_business !== undefined);
  const sellerUsers = bizUsers.filter(
    (u) => u.user_business.permissions === 'SELLER' || u.user_business.permissions === 'ADMIN',
  );
  const sellersNotInEvent = sellerUsers.filter(
    (u) => !u.user_events.find((ue) => ue.event_id === eventId),
  );
  return sellersNotInEvent;
};

const AdminSellersPage = () => {
  const navigate = useNavigate();
  const { event } = useGetSelectedEvent();
  const { eventId, businessId } = useGetParams();
  const [showModal, setShowModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [sellersNotInEvent, setSellersNotInEvent] = useState<UserDTO[]>([]);
  const { data: eventUsers, isFetching: isFetchingEventUsers } = useGetEventUsersQuery({
    businessId,
    eventId,
  });

  const {
    data: bizUsers,
    isFetching: isFetchingBizUsers,
    refetch: refetchBizUsers,
  } = useGetBusinessUsersQuery({
    businessId,
  });

  useEffect(() => {
    if (eventUsers) {
      refetchBizUsers();
    }
  }, [eventUsers]);

  useEffect(() => {
    if (bizUsers) {
      setSellersNotInEvent(getSellersNotInEvent(bizUsers, businessId, eventId));
    }
  }, [bizUsers]);

  const handleAddSeller = () => {
    setShowModal(true);
  };

  const handleSucessInviteUser = () => {
    setShowImportModal(false);
    setShowModal(false);
  };

  return (
    <div className="my-6 flex flex-col gap-8">
      <div className="flex flex-col gap-8">
        <AdminSectionTitle title="Eventos" subtitle={`${event?.name ? `${event?.name} - Vendedores` : 'Vendedores'}`} mainButtons={[
          {
            label: 'Importar vendedores',
            onClick: () => setShowImportModal(true),
            variant: 'secondary',
            icon: <ArrowDownCircle className='text-xl' />,
            hidden: isFetchingEventUsers || isFetchingBizUsers,
          },
        ]} />
        {(isFetchingEventUsers || isFetchingBizUsers) && (
          <OrdersTableSkeleton />
        )}{' '}
        {event?.id && event?.business_id && event?.show_sellers_ranking && (
          <div className="glass-surface">
            <h1 className="text-2xl font-medium mb-2">Top vendedores</h1>
            <TopSellersRanking businessId={event.business_id} eventId={event.id} title={false} />
          </div>
        )}
        <div className="glass-surface">
          <h1 className="text-2xl font-medium mb-2">Vendedores</h1>
          <SellersTable
            businessId={businessId}
            eventId={eventId}
            handleAddSeller={handleAddSeller}
          />
        </div>
        <CustomModal
          onRequestClose={() => setShowImportModal(false)}
          isOpen={showImportModal}
          actionBar={<div></div>}
        >
          <Fragment>
            <div className="flex flex-col mb-5">
              <h1 className="text-xl font-medium mb-2">Importar vendedores</h1>
              <p className="text-text-tertiary text-sm">
                Podrás importar vendedores a este evento de otro evento de tu productora
              </p>
            </div>
            {sellersNotInEvent.length > 0 && (
              <ImportSellersToEvent
                businessId={businessId}
                eventId={eventId}
                onSuccess={handleSucessInviteUser}
              />
            )}
            {sellersNotInEvent.length === 0 && (
              <div className="flex flex-col items-center justify-center">
                <IconInfoCard
                  title="No hay vendedores para importar"
                  info="Debes agregar vendedores a vendedores a la productora para poder importarlos a este evento"
                  icon={FaUserAltSlash}
                  iconColor="text-secondary-500"
                />
              </div>
            )}
          </Fragment>
        </CustomModal>
        <CustomModal
          onRequestClose={() => setShowModal(false)}
          isOpen={showModal}
          actionBar={<div></div>}
          hideCancelButton
          overflowVisible
        >
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <h1 className="text-xl font-medium">Agregar vendedor</h1>
              {sellersNotInEvent.length > 0 && <p className="text-text-tertiary text-sm">
                Podrás agregar vendedores a este evento
              </p>}
            </div>
            {sellersNotInEvent.length > 0 && (
              <AddEditSellerToEvent
                businessId={businessId}
                eventId={eventId}
                users={sellersNotInEvent}
                onSuccess={handleSucessInviteUser}
                onClose={() => setShowModal(false)}
              />
            )}

            {sellersNotInEvent.length === 0 && (
              <div className="flex flex-col gap-4">
                <div>
                  <p className="text-md text-text-secondary">No hay más vendedores en esta productora</p>
                  <p className="text-text-secondary">
                    Debes agregar vendedores a la productora para poder agregarlos a este evento
                  </p>
                </div>
                <Button variant="primary" onClick={() => navigate(`/admin/${businessId}/users`)}>
                  <div className="flex flex-row gap-2 items-center">
                    Agregar
                    <FiExternalLink />
                  </div>
                </Button>
                <Button variant="secondary" onClick={() => setShowModal(false)}>
                  Cancelar
                </Button>
              </div>
            )}
          </div>
        </CustomModal>
      </div>
    </div>
  );
};

export default AdminSellersPage;
